import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaxCategoryToken, EnumToken, CountryToken } from '@sleed/bizcommerce-api';
import { taxCategoriesApi, countriesApi } from '~/lib/api/client';
import { formToToken, type TaxCategoryFormValues } from '~/lib/schemas/tax-category';

export const taxCategoryKeys = {
	all: ['tax-categories'] as const,
	list: () => [...taxCategoryKeys.all, 'list'] as const,
	taxTypes: () => [...taxCategoryKeys.all, 'tax-types'] as const,
	taxStatuses: () => [...taxCategoryKeys.all, 'tax-statuses'] as const,
	countries: () => [...taxCategoryKeys.all, 'countries'] as const
};

export function useTaxCategoriesList() {
	return useQuery<TaxCategoryToken[]>({
		queryKey: taxCategoryKeys.list(),
		queryFn: async () => (await taxCategoriesApi.getAll()).data.items ?? []
	});
}

export function useTaxTypes() {
	return useQuery<EnumToken[]>({
		queryKey: taxCategoryKeys.taxTypes(),
		queryFn: async () => (await taxCategoriesApi.getTaxTypes()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxStatuses() {
	return useQuery<EnumToken[]>({
		queryKey: taxCategoryKeys.taxStatuses(),
		queryFn: async () => (await taxCategoriesApi.getTaxStatus()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxCategoryCountries() {
	return useQuery<CountryToken[]>({
		queryKey: taxCategoryKeys.countries(),
		queryFn: async () => (await countriesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxCategoryMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: taxCategoryKeys.list() });
	const create = useMutation({
		mutationFn: async (form: TaxCategoryFormValues) => (await taxCategoriesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: TaxCategoryFormValues) => (await taxCategoriesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await taxCategoriesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
